import { Link } from "@tanstack/react-router";
import { ChevronRight, Languages } from "lucide-react";

import { BlogMarkdown } from "@/features/blog/components/blog-markdown";
import { HelpCategoryIcon } from "@/features/help/components/help-category-icon";
import { StillStuck } from "@/features/help/components/still-stuck";
import type { HelpArticlePage } from "@/features/help/types";
import { Eyebrow } from "@/features/portal/components/portal-ui";
import { languageName } from "@/lib/languages";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import { appLanguageOf } from "@complete-athlete/enums";

const updatedOn = (iso: string) =>
  new Intl.DateTimeFormat(getLocale(), { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));

/** An article's opening, inside the head card: the way back to its topic, the question and its one-line answer. */
export const HelpArticleHero = ({ article }: { article: HelpArticlePage }) => (
  <div lang={article.language} className="relative mx-auto max-w-[820px] px-1 pt-12 pb-2 text-center md:pt-16 md:pb-6">
    <nav lang={getLocale()} aria-label={m.help_breadcrumb_label()} className="flex flex-wrap items-center justify-center gap-1.5">
      <Link to="/help" className="outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Eyebrow>{m.help_title()}</Eyebrow>
      </Link>
      <ChevronRight aria-hidden className="size-3.5 text-muted-foreground" />
      <Link
        to="/help"
        hash={article.category.slug}
        className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase transition-colors hover:text-foreground"
      >
        {article.category.name}
      </Link>
    </nav>
    <h1 className="mt-6 text-[clamp(2rem,4vw,3.25rem)] leading-[1.04] font-medium tracking-[-0.045em] text-balance">
      {article.title}
    </h1>
    {article.summary ? (
      <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">{article.summary}</p>
    ) : null}
    <p lang={getLocale()} className="mt-5 text-[13px] text-muted-foreground">{m.help_updated({ date: updatedOn(article.updatedAt) })}</p>
  </div>
);

/**
 * One article: its body set for reading, and beside it the rest of its topic and the way to a
 * person. An article not translated into the page's language says so, in the page's language,
 * and marks its text with the language it is really in.
 */
export const HelpArticleView = ({ article }: { article: HelpArticlePage }) => {
  const shownIn = appLanguageOf(article.language);
  const untranslated = shownIn && article.language !== getLocale();

  return (
    <div className="mt-3 grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
      <article lang={article.language} className="rounded-[32px] border border-border bg-background px-6 py-8 md:px-14 md:py-12">
        {untranslated ? (
          <p lang={getLocale()} className="mb-8 flex items-center gap-2.5 rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
            <Languages aria-hidden className="size-4 shrink-0" />
            {m.help_untranslated({ language: languageName(shownIn) })}
          </p>
        ) : null}
        <BlogMarkdown content={article.body} className="[&>*:first-child]:mt-0" />
      </article>

      <aside className="grid gap-3 lg:sticky lg:top-3">
        {article.siblings.length ? (
          <nav aria-labelledby="help-more-title" className="rounded-[28px] border border-border bg-background p-6">
            <div className="flex items-center gap-3">
              <HelpCategoryIcon icon={article.category.icon} className="size-9 rounded-xl [&_svg]:size-4" />
              <h2 id="help-more-title" className="text-[15px] font-medium">
                {m.help_more_in({ category: article.category.name })}
              </h2>
            </div>
            <ul className="mt-4 -mb-2 divide-y divide-border">
              {article.siblings.map((sibling) => (
                <li key={sibling.slug}>
                  <Link
                    to="/help/$slug"
                    params={{ slug: sibling.slug }}
                    className="group flex items-center justify-between gap-3 py-2.5 text-sm font-medium"
                  >
                    <span className="transition-colors group-hover:text-[var(--site-ink)]">{sibling.title}</span>
                    <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        <StillStuck compact />
      </aside>
    </div>
  );
};
